import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  DollarSign, 
  Users, 
  Calendar, 
  Ticket, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Edit,
  Trash2,
  X
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Edit Booking Modal for Dashboard
const DashboardEditModal = ({ booking, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: booking?.name || "",
    email: booking?.email || "",
    ticket_type: booking?.ticket_type || booking?.package_name || "",
    quantity: booking?.quantity || booking?.guest_count || 1,
    total_amount: booking?.amount || 0,
    notes: booking?.notes || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/landing-page-bookings/${booking.id}`, formData);
      toast.success("Booking updated successfully");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0F0F0F] border border-[rgba(212,175,55,0.2)] w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-6 border-b border-[rgba(212,175,55,0.15)]">
          <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="dashboard-edit-modal-title">
            Edit Booking
          </h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#F5F5F0]">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Customer Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              data-testid="dashboard-edit-name"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A1A1AA] mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              data-testid="dashboard-edit-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Ticket Type</label>
              <input
                type="text"
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1">Amount (C$)</label>
              <input
                type="number"
                step="0.01"
                value={formData.total_amount}
                onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-[#121212] border border-[rgba(212,175,55,0.3)] text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none"
              />
            </div>
          </div>
          <div className="flex gap-4 pt-4 border-t border-[rgba(212,175,55,0.15)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="dashboard-edit-save">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingBooking, setEditingBooking] = useState(null);
  const [deletingBooking, setDeletingBooking] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async (booking) => {
    try {
      if (booking.type === "landing_page") {
        await axios.delete(`${API}/admin/landing-page-bookings/${booking.id}`);
      } else if (booking.type === "event") {
        await axios.delete(`${API}/admin/event-bookings/${booking.id}`);
      } else if (booking.type === "experience") {
        await axios.delete(`${API}/admin/experience-bookings/${booking.id}`);
      }
      toast.success("Booking deleted");
      setDeletingBooking(null);
      fetchDashboardStats();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const COLORS = ['#D4AF37', '#8A7E57', '#CD7F32', '#722F37'];

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, trendUp }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 bg-[rgba(212,175,55,0.1)] rounded">
          <Icon className="w-6 h-6 text-[#D4AF37]" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm ${trendUp ? 'text-green-500' : 'text-red-500'}`}>
            {trendUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {trend}
          </div>
        )}
      </div>
      <h3 className="text-[#A1A1AA] text-sm mb-1">{title}</h3>
      <p className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">{value}</p>
      {subtitle && <p className="text-[#a1a1aa] text-sm mt-1">{subtitle}</p>}
    </motion.div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-2">
            Dashboard
          </h1>
          <p className="text-[#A1A1AA]">Overview of your business performance (confirmed payments only)</p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={DollarSign}
            title="Total Revenue"
            value={`C$${(stats?.total_revenue || 0).toLocaleString()}`}
            subtitle="All confirmed payments"
          />
          <StatCard
            icon={Users}
            title="Total Guests"
            value={stats?.total_guests || 0}
            subtitle="Confirmed tickets"
          />
          <StatCard
            icon={Calendar}
            title="Total Events"
            value={stats?.total_events || 0}
            subtitle="Events hosted"
          />
          <StatCard
            icon={Ticket}
            title="Total Bookings"
            value={stats?.total_bookings || 0}
            subtitle="Confirmed only"
          />
        </div>

        {/* Revenue Split */}
        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
              Revenue Overview
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.revenue_by_month || []}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.1)" />
                  <XAxis dataKey="month" stroke="#52525B" fontSize={12} />
                  <YAxis stroke="#52525B" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#121212', 
                      border: '1px solid rgba(212,175,55,0.3)',
                      color: '#F5F5F0'
                    }}
                    formatter={(value) => [`C$${value}`, '']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#D4AF37" 
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
              Revenue by Type
            </h3>
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Events', value: stats?.event_revenue || 0 },
                      { name: 'Landing Pages', value: stats?.landing_page_revenue || 0 },
                      { name: 'Experiences', value: stats?.experience_revenue || 0 }
                    ].filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {[0, 1, 2].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#121212', 
                      border: '1px solid rgba(212,175,55,0.3)',
                      color: '#F5F5F0'
                    }}
                    formatter={(value) => [`C$${value}`, '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#D4AF37]" />
                <span className="text-[#A1A1AA] text-sm">Events: C${(stats?.event_revenue || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#8A7E57]" />
                <span className="text-[#A1A1AA] text-sm">Landing Pages: C${(stats?.landing_page_revenue || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#CD7F32]" />
                <span className="text-[#A1A1AA] text-sm">Experiences: C${(stats?.experience_revenue || 0).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bookings by Event */}
        {stats?.bookings_by_event?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
          >
            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] mb-6">
              Bookings by Event
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.bookings_by_event}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.1)" />
                  <XAxis dataKey="name" stroke="#52525B" fontSize={12} />
                  <YAxis stroke="#52525B" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#121212', 
                      border: '1px solid rgba(212,175,55,0.3)',
                      color: '#F5F5F0'
                    }}
                  />
                  <Bar dataKey="guests" fill="#D4AF37" name="Guests" />
                  <Bar dataKey="revenue" fill="#8A7E57" name="Revenue (C$)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {/* Recent Bookings - Enhanced with Source, Booking Code, Edit */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0]">
              Recent Confirmed Bookings
            </h3>
            <a href="/admin/bookings" className="text-[#D4AF37] text-sm hover:underline" data-testid="view-all-bookings-link">
              View all
            </a>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="dashboard-recent-bookings-table">
              <thead>
                <tr className="border-b border-[rgba(212,175,55,0.15)]">
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Customer</th>
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Booking Code</th>
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Details</th>
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Source</th>
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-[#A1A1AA] text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_bookings?.length > 0 ? (
                  stats.recent_bookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-[rgba(212,175,55,0.1)]">
                      <td className="py-4 px-4">
                        <p className="text-[#F5F5F0]">{booking.name}</p>
                        <p className="text-[#a1a1aa] text-sm">{booking.email}</p>
                      </td>
                      <td className="py-4 px-4">
                        {booking.booking_code ? (
                          <span className="text-[#D4AF37] font-mono text-sm" data-testid="dashboard-booking-code">
                            {booking.booking_code}
                          </span>
                        ) : (
                          <span className="text-[#6B7280] text-xs">N/A</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-[#A1A1AA]">
                        {booking.type === 'event' || booking.type === 'landing_page' ? (
                          <span>{booking.event_title} - {booking.ticket_type} x {booking.quantity}</span>
                        ) : (
                          <span>{booking.package_name} - {booking.guest_count} guests</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 text-xs rounded ${
                          booking.source_type === 'manual'
                            ? 'bg-purple-900/30 text-purple-400'
                            : 'bg-blue-900/30 text-blue-400'
                        }`} data-testid="dashboard-booking-source">
                          {booking.source_display || booking.type?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-[#F5F5F0] font-medium">
                        C${booking.amount?.toFixed(2)}
                      </td>
                      <td className="py-4 px-4">
                        <span className="px-2 py-1 text-xs uppercase tracking-wider bg-green-900/30 text-green-500">
                          {booking.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingBooking(booking)}
                            className="p-2 bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 rounded transition-colors"
                            title="Edit booking"
                            data-testid="dashboard-edit-booking-btn"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletingBooking(booking)}
                            className="p-2 bg-red-900/30 text-red-400 hover:bg-red-900/50 rounded transition-colors"
                            title="Delete booking"
                            data-testid="dashboard-delete-booking-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-[#a1a1aa]">
                      No confirmed bookings yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Edit Modal */}
      {editingBooking && (
        <DashboardEditModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSave={fetchDashboardStats}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingBooking && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0F0F0F] border border-red-900/50 w-full max-w-md p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-900/30 rounded">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-xl font-['Playfair_Display'] font-bold text-[#F5F5F0]" data-testid="dashboard-delete-confirm-title">
                Delete Booking
              </h2>
            </div>
            <p className="text-[#A1A1AA] mb-2">
              Are you sure you want to delete this booking?
            </p>
            <div className="bg-[#121212] border border-[rgba(212,175,55,0.15)] p-3 mb-6 rounded">
              <p className="text-[#F5F5F0] font-medium">{deletingBooking.name}</p>
              <p className="text-[#A1A1AA] text-sm">{deletingBooking.email}</p>
              {deletingBooking.booking_code && (
                <p className="text-[#D4AF37] font-mono text-sm mt-1">{deletingBooking.booking_code}</p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setDeletingBooking(null)}
                className="btn-secondary flex-1"
                data-testid="dashboard-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBooking(deletingBooking)}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
                data-testid="dashboard-delete-confirm"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboardPage;
